/**
 * Admin Sidebar Component
 * Handles navigation, theme preview, and user info
 */
import { memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coffee, LogOut, X, ChevronRight, Sun, Moon, Monitor, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConnectionStatus from '@/components/admin/shared/ConnectionStatus';
import { usePermissions } from '@/context/PermissionsContext';

function AdminSidebar({
  admin,
  sidebarOpen,
  setSidebarOpen,
  activeTab,
  setActiveTab,
  expandedSections,
  setExpandedSections,
  navSections,
  previewMode,
  isDarkMode,
  startPreview,
  exitPreview,
  onLogout
}) {
  const { filterSections, isOwner, loading: permissionsLoading } = usePermissions();

  // Filter sections based on user permissions
  const filteredSections = permissionsLoading ? [] : filterSections(navSections);

  const handleSectionClick = useCallback((section) => {
    if (section.children.length > 0) {
      setExpandedSections(prev => ({
        ...prev,
        [section.id]: !prev[section.id]
      }));
    } else {
      setActiveTab(section.id);
      setSidebarOpen(false);
    }
  }, [setExpandedSections, setActiveTab, setSidebarOpen]);

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
  }, [setActiveTab, setSidebarOpen]);

  return (
    <aside className={`fixed left-0 top-0 h-full w-72 lg:w-64 bg-card border-r border-border flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${
      sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
    }`}>
      {/* Header - Fixed */}
      <div className="flex items-center justify-between p-4 lg:p-6 lg:pb-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <Coffee className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-lg text-foreground">Bulaccino Cafe</h2>
            <p className="text-xs text-muted-foreground">Admin Panel</p>
          </div>
        </div>
        {/* Mobile close button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Edit Mode Controls */}
      <div className="px-4 lg:px-6 pb-3 flex-shrink-0">
        {/* Removed drag-to-reorder for hierarchical navigation */}
      </div>

      {/* Hierarchical Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto px-4 lg:px-6 pb-4 space-y-1 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
        {permissionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : (
          filteredSections.map(section => {
            const SectionIcon = section.icon;
            const hasChildren = section.children.length > 0;
            const isExpanded = expandedSections[section.id];
            const isActive = hasChildren 
              ? section.children.some(child => child.id === activeTab)
              : section.id === activeTab;
            
            return (
              <div key={section.id} className="mb-1">
                {/* Section Header */}
                <button
                  onClick={() => handleSectionClick(section)}
                  data-testid={`admin-section-${section.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    !hasChildren && activeTab === section.id
                      ? 'bg-primary text-white'
                      : isActive && hasChildren
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <SectionIcon className="w-5 h-5" />
                  <span className="flex-1 text-left truncate">{section.label}</span>
                  {hasChildren && (
                    <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                  )}
                </button>
                
                {/* Children (Sub-tabs) */}
                {hasChildren && (
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-muted pl-3">
                          {section.children.map(child => {
                            const ChildIcon = child.icon;
                            return (
                              <button
                                key={child.id}
                                onClick={() => handleTabClick(child.id)}
                                data-testid={`admin-tab-${child.id}`}
                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                  activeTab === child.id
                                    ? 'bg-primary text-white'
                                    : 'hover:bg-muted text-foreground'
                                }`}
                              >
                                <ChildIcon className="w-4 h-4" />
                                <span className="truncate">{child.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })
        )}
      </nav>

      {/* Footer - Fixed */}
      <div className="p-4 lg:p-6 lg:pt-4 flex-shrink-0 border-t border-border">
        {/* Theme Preview Panel */}
        <div className="mb-4 p-3 bg-muted/50 rounded-xl border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Monitor className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Theme Preview</span>
          </div>
          {previewMode ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground">
                  Previewing: <span className="font-bold capitalize">{previewMode}</span>
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="w-full text-xs rounded-lg border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                onClick={exitPreview}
                data-testid="exit-preview-btn"
              >
                <X className="w-3 h-3 mr-1" />
                Exit Preview
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={!isDarkMode && !previewMode ? 'default' : 'outline'}
                className="flex-1 text-xs rounded-lg"
                onClick={() => startPreview('light')}
                data-testid="preview-light-btn"
              >
                <Sun className="w-3 h-3 mr-1" />
                Light
              </Button>
              <Button
                size="sm"
                variant={isDarkMode && !previewMode ? 'default' : 'outline'}
                className="flex-1 text-xs rounded-lg"
                onClick={() => startPreview('dark')}
                data-testid="preview-dark-btn"
              >
                <Moon className="w-3 h-3 mr-1" />
                Dark
              </Button>
            </div>
          )}
          {!previewMode && (
            <p className="text-[10px] text-muted-foreground mt-2 text-center">
              Preview how customers see your site
            </p>
          )}
        </div>

        <div className="p-3 lg:p-4 bg-muted rounded-xl mb-3 lg:mb-4">
          <p className="text-sm font-medium text-foreground">{admin.name}</p>
          <p className="text-xs text-muted-foreground capitalize">{admin.role}</p>
        </div>
        
        {/* Connection Status Indicator */}
        <div className="mb-3 lg:mb-4">
          <ConnectionStatus />
        </div>
        
        <Button 
          variant="outline" 
          className="w-full rounded-xl"
          onClick={onLogout}
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </aside>
  );
}

export default memo(AdminSidebar);
